import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getGroup, getGroupMembers, updateGroupMember, removeGroupMember } from '../lib/firestore/groups';
import { canInviteToGroup } from '../lib/permissions';
import InviteButton from '../components/InviteButton';
import SponsorMember from '../components/SponsorMember';
import '../styles/GroupMembers.css';

const GroupMembers = () => {
  const { groupId } = useParams();
  const { user } = useAuthState();
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [canInvite, setCanInvite] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  useEffect(() => {
    if (groupId && user) {
      loadMembers();
    } else if (!user) {
      navigate('/login');
    }
  }, [groupId, user, navigate]);

  const loadMembers = async () => {
    if (!groupId || !user) return;

    setLoading(true);
    try {
      const groupData = await getGroup(groupId);
      setGroup(groupData);

      const membersList = await getGroupMembers(groupId);
      setMembers(membersList);

      const userMember = membersList.find((m) => m.userId === user.uid);
      setUserRole(userMember?.role || null);

      const canInv = await canInviteToGroup(groupId, user.uid);
      setCanInvite(canInv);
    } catch (err) {
      setError(err.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await removeGroupMember(groupId, memberId);
      await loadMembers();
    } catch (err) {
      alert('Failed to remove member: ' + err.message);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await updateGroupMember(groupId, memberId, { role: newRole });
      await loadMembers();
      setEditingMember(null);
    } catch (err) {
      alert('Failed to update role: ' + err.message);
    }
  };

  const handleLeaveGroup = async () => {
    if (!confirm('Are you sure you want to leave this group? You\'ll lose access to all group events.')) {
      return;
    }

    try {
      await removeGroupMember(groupId, user.uid);
      navigate('/home');
    } catch (err) {
      alert('Failed to leave group: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="members-container">
        <div className="loading">Loading members...</div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="members-container">
        <div className="error-message">{error || 'Group not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  const isOwner = userRole === 'owner';
  const isCohost = userRole === 'cohost';
  const canManage = isOwner || isCohost;

  return (
    <div className="members-container">
      <header className="members-header">
        <div className="members-header-content">
          <button className="btn-back" onClick={() => navigate(`/groups/${groupId}`)}>
            ← Back
          </button>
          <h1>Members</h1>
          <p>{group.name}</p>
        </div>
        {canInvite && (
          <div className="members-header-actions">
            <InviteButton
              targetKind="group_join"
              targetId={groupId}
            />
          </div>
        )}
      </header>

      <main className="members-main">
        <div className="members-stats">
          <div className="stat-card">
            <div className="stat-value">{members.length}</div>
            <div className="stat-label">Total Members</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {members.filter((m) => m.role === 'owner' || m.role === 'cohost').length}
            </div>
            <div className="stat-label">Hosts</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {members.filter((m) => m.sponsoredBy).length}
            </div>
            <div className="stat-label">Sponsored</div>
          </div>
        </div>

        {/* Sponsorship Section */}
        {(isOwner || isCohost) && group.rules.maxSponsorsPerMember !== undefined && (
          <div className="sponsor-section">
            <SponsorMember groupId={groupId} />
          </div>
        )}

        {/* Leave Group Section */}
        {!isOwner && !isCohost && isMember && (
          <section className="leave-group-section">
            <h3>Leave Group</h3>
            <p>You can leave this group at any time. You'll lose access to all group events and message boards.</p>
            <button
              className="btn btn-secondary"
              onClick={handleLeaveGroup}
            >
              Leave Group
            </button>
          </section>
        )}

        <div className="members-list-section">
          <h2>All Members</h2>
          <div className="members-list">
            {members.map((member) => (
              <div key={member.userId} className="member-row">
                <div className="member-info">
                  <div className="member-id-display">
                    {member.userId === user?.uid ? 'You' : `User ${member.userId.slice(0, 8)}...`}
                  </div>
                  <div className="member-meta">
                    <span className={`role-badge role-${member.role}`}>{member.role}</span>
                    {member.sponsoredBy && (
                      <span className="sponsored-label">Sponsored</span>
                    )}
                    <span className="joined-date">
                      Joined {new Date(member.joinedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {canManage && member.userId !== user?.uid && (
                  <div className="member-actions">
                    {isOwner && (
                      <>
                        {editingMember === member.userId ? (
                          <div className="role-editor">
                            <select
                              className="input input-sm"
                              value={member.role}
                              onChange={(e) => handleUpdateRole(member.userId, e.target.value)}
                            >
                              <option value="member">Member</option>
                              <option value="cohost">Cohost</option>
                              <option value="sponsored_member">Sponsored Member</option>
                            </select>
                            <button
                              className="btn btn-sm"
                              onClick={() => setEditingMember(null)}
                            >
                              Done
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingMember(member.userId)}
                            >
                              Edit Role
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleRemoveMember(member.userId)}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default GroupMembers;

